import { useNavigate } from "react-router-dom";
import {
  Calendar, Repeat, Plane, MapPin, Settings, Eye, Share2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { eventCalendarDate } from "@/lib/dates";

export const currencySymbol = (c) =>
  ({ EUR: "€", GBP: "£", USD: "$" }[(c || "USD").toUpperCase()] || "$");

export const formatMoneyMap = (map) => {
  if (!map || Object.keys(map).length === 0) return "—";
  return Object.entries(map)
    .sort(([, a], [, b]) => b - a)
    .map(([c, v]) => `${currencySymbol(c)}${Math.round(v).toLocaleString()}`)
    .join(" · ");
};

export const formatRevenue = (revenueByCcy) => {
  if (!revenueByCcy || Object.keys(revenueByCcy).length === 0)
    return { primary: "—", secondary: null };
  const sorted = Object.entries(revenueByCcy).sort(([, a], [, b]) => b - a);
  const [primaryCcy, primaryAmt] = sorted[0];
  return {
    primary: `${currencySymbol(primaryCcy)}${Math.round(primaryAmt).toLocaleString()}`,
    secondary: sorted.length > 1 ? `+ ${sorted.length - 1} more` : null,
  };
};

export const formatEventDate = (iso, endIso) => {
  if (!iso) return "—";
  const d = eventCalendarDate(iso);
  if (!d) return "—";
  const opts = { weekday: "short", day: "numeric", month: "short" };
  const start = d.toLocaleDateString("en-GB", opts);
  if (endIso) {
    const e = eventCalendarDate(endIso);
    if (e && e.toDateString() !== d.toDateString()) {
      return `${start} → ${e.toLocaleDateString("en-GB", opts)}`;
    }
  }
  return start;
};

export const KpiCard = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <Card
    className="p-4 border-border"
    data-testid={testid || `kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="font-display text-2xl sm:text-3xl mt-1 leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      {Icon && (
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent || "bg-primary/15 text-primary-ink"}`}>
          <Icon className="w-4 h-4" />
        </div>
      )}
    </div>
  </Card>
);

export const TypeBadge = ({ type }) => {
  const cfg = {
    game: { label: "Session", className: "bg-blue-500/15 text-blue-700 dark:text-blue-300", Icon: Calendar },
    series: { label: "Series", className: "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300", Icon: Repeat },
    trip: { label: "Retreat", className: "bg-amber-500/15 text-amber-700 dark:text-amber-300", Icon: Plane },
  }[type] || { label: type, className: "bg-muted text-muted-foreground", Icon: Calendar };
  const { label, className, Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${className}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
};

export const EventRow = ({ event, onShare }) => {
  const navigate = useNavigate();
  const fillPct = event.max_count > 0 ? Math.min(100, Math.round((event.current_count / event.max_count) * 100)) : 0;
  const isCancelled = event.status === "cancelled";

  return (
    <Card
      className={`p-4 border-border hover:border-primary/40 transition-colors ${isCancelled ? "opacity-60" : ""}`}
      data-testid={`host-event-row-${event.id}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <img
          src={event.image_url}
          alt=""
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover shrink-0 cursor-pointer"
          onClick={() => navigate(event.url)}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <TypeBadge type={event.type} />
                {isCancelled && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-500/15 text-red-700 dark:text-red-300">
                    Cancelled
                  </span>
                )}
                {event.type === "series" && event.total_sessions > 0 && (
                  <span className="text-[11px] text-muted-foreground">· {event.total_sessions} sessions</span>
                )}
              </div>
              <h3
                className="font-display text-base sm:text-lg leading-tight truncate cursor-pointer hover:text-primary-ink"
                onClick={() => navigate(event.url)}
              >
                {event.title}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatEventDate(event.date, event.end_date)}
                </span>
                {event.location_name && (
                  <span className="flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" />
                    {event.location_name}{event.city ? `, ${event.city}` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <div className="flex items-center gap-4 text-xs">
              <div>
                <span className="font-semibold text-foreground">{event.current_count}</span>
                <span className="text-muted-foreground">/{event.max_count || "∞"}</span>
                <span className="text-muted-foreground ml-1">booked</span>
                {event.max_count > 0 && (
                  <div className="w-20 h-1 rounded-full bg-muted mt-1 overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${fillPct}%` }} />
                  </div>
                )}
              </div>
              {event.price > 0 && (
                <div>
                  <p className="text-muted-foreground">Revenue</p>
                  <p className="font-semibold text-foreground">
                    {currencySymbol(event.currency)}{Math.round(event.revenue).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="sm"
                onClick={() => navigate(`/host/manage/${event.type}/${event.id}`)}
                className="h-8 gap-1"
                data-testid={`host-event-manage-${event.id}`}
              >
                <Settings className="w-3.5 h-3.5" /> Manage
              </Button>
              <Button
                variant="ghost" size="sm"
                onClick={() => navigate(event.url)}
                className="h-8 gap-1"
                data-testid={`host-event-view-${event.id}`}
              >
                <Eye className="w-3.5 h-3.5" /> View
              </Button>
              {onShare && (
                <Button
                  variant="ghost" size="sm"
                  onClick={() => onShare(event)}
                  className="h-8 gap-1"
                  data-testid={`host-event-share-${event.id}`}
                >
                  <Share2 className="w-3.5 h-3.5" /> Share
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/** Small "Preview" pill for areas that are visually complete but not yet wired to the backend. */
export const PreviewBadge = () => (
  <span
    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-primary/15 text-primary-ink"
    data-testid="preview-badge"
  >
    Preview
  </span>
);

/** Consistent page header used across every Host OS screen. */
export const HostPageHeader = ({ icon: Icon, title, subtitle, preview, actions }) => (
  <div className="flex items-start sm:items-end justify-between gap-3 flex-wrap mb-5">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl flex items-center gap-2.5">
        {Icon && <Icon className="w-7 h-7 text-primary-ink" />} {title}
        {preview && <PreviewBadge />}
      </h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);
